import {
  offset,
  type Placement,
  type Strategy,
  useFloating,
  flip,
  shift,
  FloatingOverlay,
  useTransitionStyles,
} from "@floating-ui/react";
import dotsIcon from "@tabler/icons/outline/dots.svg";
import { supportsPassiveEvents } from "detect-passive-events";
import React, { useEffect, useState } from "react";

import { useMergedRefs } from "@/hooks/useMergedRefs";
import { cn } from "@/utils/cn";

import { IconButton, Portal } from "../ui";

import DropdownMenuItem, { type MenuItem } from "./dropdown-menu-item";

import type { Status } from "@/schemas";

export type Menu = Array<MenuItem | null>;

const widths = {
  normal: "w-56",
  medium: "w-60",
  wide: "w-[300px]",
  content: "w-max",
};

type MenuWidth = keyof typeof widths;

interface DropdownTriggerProps {
  disabled?: boolean;
  onClick?: React.EventHandler<
    React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>
  >;
  onKeyPress?: React.EventHandler<React.KeyboardEvent<HTMLButtonElement>>;
  ref?: React.Ref<HTMLElement>;
  primaryAction?: Record<string, unknown>;
}

interface DropdownMenuProps {
  children?: React.ReactElement<DropdownTriggerProps>;
  disabled?: boolean;
  isPortal?: boolean;
  items: Menu;
  onClose?: () => void;
  onOpen?: () => void;
  onShiftClick?: React.EventHandler<React.MouseEvent | React.KeyboardEvent>;
  placement?: Placement;
  position?: Strategy;
  src?: string;
  status?: Status;
  title?: string;
  width?: MenuWidth;
  containerClassName?: string;
  dropdownClassName?: string;
}

const listenerOptions = supportsPassiveEvents ? { passive: true } : false;

const DropdownMenu = (props: DropdownMenuProps) => {
  const {
    children,
    disabled,
    isPortal = true,
    items,
    onClose,
    onOpen,
    onShiftClick,
    placement: initialPlacement = "top",
    position = "absolute",
    src = dotsIcon,
    title = "Menu",
    width = "normal",
    containerClassName,
    dropdownClassName,
  } = props;

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const { x, y, strategy, refs, update, context } =
    useFloating<HTMLButtonElement>({
      open: isOpen,
      onOpenChange: (value) => {
        setIsOpen(value);

        if (value && onOpen) {
          onOpen();
        }
      },
      placement: initialPlacement,
      strategy: position,
      middleware: [
        offset(8),
        flip(),
        shift({
          padding: 8,
        }),
      ],
    });

  const { isMounted, styles } = useTransitionStyles(context, {
    initial: {
      opacity: 0,
      transform: "scale(0.8)",
    },
    duration: {
      open: 200,
      close: 200,
    },
  });

  const triggerRef = useMergedRefs<HTMLElement>(
    refs.setReference,
    children?.props.ref,
  );

  const Container = isPortal ? Portal : React.Fragment;

  const handleClick: React.EventHandler<
    React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>
  > = (event) => {
    event.stopPropagation();
    event.preventDefault();

    if (onShiftClick && event.shiftKey) {
      onShiftClick(event);
      return;
    }

    if (isOpen) {
      handleClose();
    } else {
      handleOpen();
    }
  };

  /**
   * On mobile screens, let's replace the Popper dropdown with a Modal.
   */
  const handleOpen = () => {
    setIsOpen(true);

    if (onOpen) {
      onOpen();
    }
  };

  const handleClose = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    (refs.reference.current as HTMLButtonElement)?.focus();

    setIsOpen(false);

    if (onClose) {
      onClose();
    }
  };

  const handleKeyPress: React.EventHandler<
    React.KeyboardEvent<HTMLButtonElement>
  > = (event) => {
    switch (event.key) {
      case " ":
      case "Enter":
        event.stopPropagation();
        event.preventDefault();
        handleClick(event);
        break;
    }
  };

  const handleDocumentClick = (event: Event) => {
    if (
      refs.floating.current &&
      !refs.floating.current.contains(event.target as Node)
    ) {
      handleClose();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!refs.floating.current) return;

    const items = Array.from(refs.floating.current.getElementsByTagName("a"));
    const index = items.indexOf(document.activeElement as any);

    let element = null;

    switch (e.key) {
      case "ArrowDown":
        element = items[index + 1] || items[0];
        break;
      case "ArrowUp":
        element = items[index - 1] || items[items.length - 1];
        break;
      case "Tab":
        if (e.shiftKey) {
          element = items[index - 1] || items[items.length - 1];
        } else {
          element = items[index + 1] || items[0];
        }
        break;
      case "Home":
        element = items[0];
        break;
      case "End":
        element = items[items.length - 1];
        break;
      case "Escape":
        handleClose();
        break;
    }

    if (element) {
      element.focus();
      e.preventDefault();
      e.stopPropagation();
    }
  };

  useEffect(() => {
    if (isOpen) {
      if (refs.floating.current) {
        (
          refs.floating.current?.querySelector(
            "li a[role='button']",
          ) as HTMLAnchorElement
        )?.focus();
      }

      document.addEventListener("click", handleDocumentClick, false);
      document.addEventListener("keydown", handleKeyDown, false);
      document.addEventListener(
        "touchend",
        handleDocumentClick,
        listenerOptions,
      );

      return () => {
        document.removeEventListener("click", handleDocumentClick);
        document.removeEventListener("keydown", handleKeyDown);
        document.removeEventListener("touchend", handleDocumentClick);
      };
    }
  }, [isOpen, refs.floating.current]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div className={cn("relative flex justify-center", containerClassName)}>
      {children ? (
        React.cloneElement(children, {
          disabled,
          onClick: children.props.primaryAction ? undefined : handleClick,
          onKeyPress: handleKeyPress,
          ref: triggerRef,
          ...(children.props.primaryAction && {
            primaryAction: {
              ...children.props.primaryAction,
              handler: handleClick,
            },
          }),
        })
      ) : (
        <IconButton
          disabled={disabled}
          className={cn(
            "text-gray-600 hover:text-gray-700 dark:hover:text-gray-500",
            {
              "text-gray-700 dark:text-gray-500": isOpen,
            },
          )}
          title={title}
          src={src}
          onClick={handleClick}
          onKeyPress={handleKeyPress}
          ref={refs.setReference}
        />
      )}

      {isMounted ? (
        <Container>
          <FloatingOverlay onClick={handleClose} />

          <div
            data-testid="dropdown-menu"
            ref={refs.setFloating}
            className={cn(
              `z-1003 p-1 ${widths[width]} rounded-md bg-white shadow-lg transition-opacity duration-100 focus:outline-hidden dark:bg-gray-900 dark:ring-2 dark:ring-gray-800`,
              {
                "pointer-events-none opacity-0": !isOpen,
              },
              dropdownClassName,
            )}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
              ...styles,
            }}
          >
            <ul>
              {items.map((item, idx) => (
                <DropdownMenuItem
                  key={idx}
                  item={item}
                  index={idx}
                  onClick={handleClose}
                  handleLayout={update}
                />
              ))}
            </ul>
          </div>
        </Container>
      ) : null}
    </div>
  );
};

export default DropdownMenu;
